/**
 * Per-user "Absender fuer mich privat markieren" (P2 hardening 04).
 *
 * STRICTLY separate from the per-mail Privat-Markierung (communication.is_private): this hides a
 * SENDER from only the caller's Posteingang (list + counts + Posteingang search); the mail stays
 * reachable in /kommunikation + search. Colleagues are unaffected. live → /posteingang/sender-private,
 * mock → an in-memory list.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SenderPrivateDto,
  SenderPrivateMarkRequest,
  SenderPrivatePreviewResponse,
  SenderPrivateUnmarkRequest,
} from '@brokly/shared';
import { apiClient } from '../../lib/api-client';
import { isLiveData } from '../../lib/data-mode';
import { qk } from '../../lib/query-keys';

// ── Mock leg ────────────────────────────────────────────────────────────────────
const MOCK_PRIVATE: SenderPrivateDto[] = [
  {
    id: 'mock-private-1',
    address: 'newsletter@werbung-beispiel.ch',
    scope: 'user',
    hide_from: null,
    hide_until: null,
    open: true,
    marked_at: '2026-09-15T09:00:00.000Z',
  },
];
let mockCounter = 2;

/** Test hygiene: restore the demo list. */
export function resetSenderPrivateMock(): void {
  MOCK_PRIVATE.splice(0, MOCK_PRIVATE.length, {
    id: 'mock-private-1',
    address: 'newsletter@werbung-beispiel.ch',
    scope: 'user',
    hide_from: null,
    hide_until: null,
    open: true,
    marked_at: '2026-09-15T09:00:00.000Z',
  });
}

/** The lower-cased addresses currently hidden for the caller (open rows) — for the mock list filter. */
export function mockPrivateAddresses(): Set<string> {
  return new Set(MOCK_PRIVATE.filter((r) => r.open).map((r) => r.address));
}

function mockMark(body: SenderPrivateMarkRequest): SenderPrivateDto {
  const address = body.address.trim().toLowerCase();
  const existing = MOCK_PRIVATE.find((r) => r.address === address && r.open);
  if (existing) {
    if (body.include_past) existing.hide_from = null;
    return existing;
  }
  const row: SenderPrivateDto = {
    id: `mock-private-${mockCounter++}`,
    address,
    scope: 'user',
    hide_from: body.include_past ? null : new Date().toISOString(),
    hide_until: null,
    open: true,
    marked_at: new Date().toISOString(),
  };
  MOCK_PRIVATE.push(row);
  return row;
}

function mockUnmark(id: string, body: SenderPrivateUnmarkRequest): SenderPrivateDto {
  const i = MOCK_PRIVATE.findIndex((r) => r.id === id);
  if (i < 0) return MOCK_PRIVATE[0]!;
  const row = MOCK_PRIVATE[i]!;
  if (body.reveal_past) {
    MOCK_PRIVATE.splice(i, 1); // list shows active rows only; a revealed row drops out
    return { ...row, open: false, hide_until: new Date().toISOString() };
  }
  row.open = false;
  row.hide_until = new Date().toISOString();
  return row;
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
/** The caller's active private-sender rows. */
export function useSenderPrivateList() {
  const live = isLiveData();
  return useQuery<SenderPrivateDto[]>({
    queryKey: [...qk.senderPrivate(), live ? 'live' : 'mock'],
    queryFn: async () => {
      if (!live) return [...MOCK_PRIVATE];
      return (await apiClient.posteingang.senderPrivate.list()).items;
    },
  });
}

/** The dialog state for one address (counts + firm/intern flags); only fetched when enabled. */
export function useSenderPrivatePreview(address: string | null, enabled: boolean) {
  const live = isLiveData();
  return useQuery<SenderPrivatePreviewResponse>({
    queryKey: [...qk.senderPrivate(), 'preview', address, live ? 'live' : 'mock'],
    enabled: enabled && !!address,
    queryFn: async () => {
      const addr = (address ?? '').trim().toLowerCase();
      if (!live) {
        return {
          address: addr,
          is_private: mockPrivateAddresses().has(addr),
          open_row_id: MOCK_PRIVATE.find((r) => r.address === addr && r.open)?.id ?? null,
          past_count: 3,
          oldest_received_at: '2026-08-01T08:00:00.000Z',
          firm_blocked: false,
          firm_hidden_past: false,
          is_internal_sender: false,
        };
      }
      return apiClient.posteingang.senderPrivate.preview(addr);
    },
  });
}

function invalidate(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: qk.senderPrivate() });
  void qc.invalidateQueries({ queryKey: ['posteingang', 'list'] });
  void qc.invalidateQueries({ queryKey: ['posteingang', 'counts'] });
}

/** Mark a sender private for the caller. */
export function useMarkSenderPrivate() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: async (body: SenderPrivateMarkRequest): Promise<SenderPrivateDto> => {
      const payload = { ...body, address: body.address.trim().toLowerCase() };
      if (live) return apiClient.posteingang.senderPrivate.mark(payload);
      return mockMark(payload);
    },
    onSuccess: () => invalidate(qc),
  });
}

/** Stop hiding a sender for the caller (from-now or all). */
export function useUnmarkSenderPrivate() {
  const qc = useQueryClient();
  const live = isLiveData();
  return useMutation({
    mutationFn: async (vars: { id: string; reveal_past: boolean }): Promise<SenderPrivateDto> => {
      if (live) return apiClient.posteingang.senderPrivate.unmark(vars.id, { reveal_past: vars.reveal_past });
      return mockUnmark(vars.id, { reveal_past: vars.reveal_past });
    },
    onSuccess: () => invalidate(qc),
  });
}
